<template>
  <div>
    <alert v-if="showFlash" class="mb-4" type="success" dismissible>Video saved.</alert>

    <form class="simple_form form edit_video" id="edit_video">

      <div class="grid lg:grid-cols-12 gap-8">

        <div class="relative lg:col-span-4 lg:order-2">
          <btn v-if="!preparingStatus && videoUrl" class="mb-4" variant="secondary" :link="true" tag="a" :href="`/videos/${this.slug}/replacement`">Replace Video</btn>
          <div class="sticky top-4 bg-neutral-200 rounded-lg p-4">
            <alert v-if="preparingStatus && !videoUrl">Main video processing. Reload shortly to check status.</alert>
            <alert v-else-if="preparingStatus && videoUrl">Video has been changed but has not finished processing. Reload shortly to check status.</alert>
            <video v-if="!preparingStatus && videoUrl" ref="mainVideojs" class="video video-js vjs-fluid vjs-big-play-centered"
                  controls="true"
                  allowfullscreen="true"
                  webkitallowfullscreen="true"
                  mozallowfullscreen="true"
                  x-webkit-airplay='allow'>
              <source :src="videoUrl" type="application/x-mpegURL" />
            </video>

            <form-inputs class="stacked previewControls mt-4 border-t border-t-neutral-300 pt-4">
              <h3>Preview</h3>

              <alert v-if="previewProcessing">Preview video processing. Reload in a few seconds to check status.</alert>
              <template v-else-if="!previewProcessing && !previewUrl">
                <div class="form-input preview_start">
                  <label class="form-label string" for="preview_start">
                    Start Time for Preview (m:ss, eg 3:35)
                  </label>
                  <div class="form-input-main">
                    <input v-model="previewStart" class="xs string" type="text" id="preview_start" />
                    <small v-if="hasErrors('preview_start')" small class="form-error">{{errors.preview_start[0]}}</small>
                  </div>
                </div>
                <div class="form-input preview_end">
                  <label class="form-label string" for="preview_end">
                    End Time for Preview (m:ss, eg 3:35)
                  </label>
                  <div class="form-input-main">
                    <input v-model="previewEnd" class="xs string" type="text" id="preview_end" />
                    <small v-if="hasErrors('preview_end')" small class="form-error">{{errors.preview_end[0]}}</small>
                  </div>
                </div>
              </template>
              <template v-else-if="previewUrl">
                <video ref="previewVideojs" class="video video-js vjs-fluid vjs-big-play-centered"
                                            controls="true" allowfullscreen="true"
                                                            webkitallowfullscreen="true"
                                                            mozallowfullscreen="true"
                                                            x-webkit-airplay='allow'>
                  <source :src="previewUrl" type="application/x-mpegURL" />
                </video>
                <button class="button-secondary mt-4" accesskey="d" @click.prevent="deletePreview" :disabled="isSubmitting">
                  Delete Preview
                </button>
              </template>
            </form-inputs>
          </div>
        </div>

        <div class="lg:col-span-8">
          <div class="form_errors rounded-md p-4 bg-error-100" ref="errorSummary"  v-show="this.errors">
            <p v-if="hasErrors('base')" style="color: #cc4b37;">{{errors.base[0]}}</p>
            <p v-if="this.errors && !hasErrors('base')" style="color: #cc4b37;">There was an error saving your video — check below.</p>
          </div>

          <form-heading title="Video Details" description="Configure the information that will
            appear for this video" />

          <form-inputs>

            <div class="form-input string required video_name" v-bind:class="{ error: hasErrors('name') }">
              <label class="form-label string required" for="video_name">
                Name
                <abbr title="required">*</abbr>
              </label>
              <div class="form-input-main">
                <input v-model="name" autofocus="autofocus" class="string required" type="text" id="video_name" required />
                <small v-if="hasErrors('name')" small class="form-error">{{errors.name[0]}}</small>
              </div>
            </div>

            <div class="form-input string required video_description" v-bind:class="{ error: hasErrors('description') }">
              <label class="form-label string required" for="video_description">
                Description
                <abbr title="required">*</abbr>
              </label>
              <div class="form-input-main">
                <textarea v-model="description" rows="4" class="string required" id="video_description" required />
                <small v-if="hasErrors('description')" small class="form-error">{{errors.description[0]}}</small>
              </div>
            </div>

            <template>
              <div v-if="isSecure && stripeConfirmed">

                <div class="form-input">
                  <label class="form-label">
                    Pricing
                  </label>
                  <div class="form-input-main">
                  <select v-model="pricingModel">
                    <option value="free">Free</option>
                    <option value="paid">Fixed price</option>
                    <option value="pay_what_you_can">Pay what you can</option>
                  </select>
                  </div>
                </div>

                <div v-if="pricingModel == 'paid'" class="form-input string video_price" v-bind:class="{ error: hasErrors('price') }">
                  <label class="form-label string" for="video_price">
                    Premium Price
                  </label>
                  <div class="form-input-main">
                    <input v-model="price" autofocus="autofocus" class="string" type="text" id="video_price" required placeholder="4.95" :disabled="purchases" />
                    <small v-if="hasErrors('price')" small class="form-error">{{errors.price[0]}}</small>
                    <small class="form-note" v-if="purchases">Because there are purchases/views, price can no longer be changed. </small>
                    <div v-if="!purchases">
                      <small>The final amount deposited in your account will be reduced by:</small>
                      <ol>
                        <li><small>- Stripe's fee of 2.9% + $0.30</small></li>
                        <li><small>- Pennant's fee (varies based on your plan)</small></li>
                      </ol>
                      <!-- <p>For example, if the customer price is $4.95, the Stripe fee will be $0.44, and -->
                      <!-- the Pennant fee is always $1.00, resulting in $3.51 deposited to your account.</p> -->
                    </div>
                  </div>
                </div>

                <div v-if="pricingModel == 'pay_what_you_can'">

                  <p v-if="minimumPriceError" class="pb-6 pt-6" style="color: #cc4b37"><strong>{{minimumPriceError}}</strong></p>

                  <div class="form-input string video_price" v-bind:class="{ error: hasErrors('price') }">
                    <label class="form-label string" for="video_price_default">
                      Default Price
                      <abbr title="required">*</abbr>
                    </label>
                    <div class="form-input-main">
                      <input v-model="price" autofocus="autofocus" class="string" type="text" id="video_price_default" required placeholder="4.95" :disabled="purchases" />
                      <small v-if="hasErrors('price')" small class="form-error">{{errors.price[0]}}</small>
                      <small class="form-note" v-if="purchases">Because there are purchases/views, price can no longer be changed. </small>
                      <div v-if="!purchases">
                        <small>The final amount deposited in your account will be reduced by:</small>
                        <ol>
                          <li><small>- Stripe's fee of 2.9% + $0.30</small></li>
                          <li><small>- Pennant's fee (varies based on your plan)</small></li>
                        </ol>
                        <!-- <p>For example, if the customer price is $4.95, the Stripe fee will be $0.44, and -->
                        <!-- the Pennant fee is always $1.00, resulting in $3.51 deposited to your account.</p> -->
                      </div>
                    </div>
                  </div>

                  <div class="form-input string video_price" v-bind:class="{ error: hasErrors('price') }">
                    <label class="form-label string" for="video_price_b">
                      Second Price Option
                      <abbr title="required">*</abbr>
                    </label>
                    <div class="form-input-main">
                      <input v-model="priceB" autofocus="autofocus" class="string" type="text" id="video_price_b" required :disabled="purchases" />
                      <small v-if="hasErrors('price_b')" small class="form-error">{{errors.price_b[0]}}</small>
                    </div>
                  </div>

                  <div class="form-input string video_price" v-bind:class="{ error: hasErrors('price') }">
                    <label class="form-label string" for="video_price_c">
                      Third Price Option
                      <abbr title="required">*</abbr>
                    </label>
                    <div class="form-input-main">
                      <input v-model="priceC" autofocus="autofocus" class="string" type="text" id="video_price_b" required  :disabled="purchases" />
                      <small v-if="hasErrors('price_c')" small class="form-error">{{errors.price_c[0]}}</small>
                    </div>
                  </div>

                  <div class="form-input string video_price" v-bind:class="{ error: hasErrors('price') }">
                    <label class="form-label string" for="video_price_min">
                      Minimum Price
                      <abbr title="required">*</abbr>
                    </label>
                    <div class="form-input-main">
                      <input v-model="priceMin" autofocus="autofocus" class="string" type="text" id="video_price_min" required :disabled="purchases" />
                      <small>Users will not be able to specify a minimum price below this amount.</small>

                      <small v-if="hasErrors('price_min')" small class="form-error">{{errors.price_min[0]}}</small>
                    </div>
                  </div>

                </div>

              </div>
              <div v-else class="form-input">
                <label class="form-label string required" for="video_price">
                  Premium
                </label>
                <div class="form-input-main">
                  <alert v-if="!isSecure" type="warning">Only secure videos can have a price set. A video can only be
                    marked secure when it is first uploaded. If this video requires a price,
                    you can delete this record with the button at the bottom of the page, and then
                    re-upload it after choosing the 'secure' option.</alert>
                  <alert v-if="isSecure && !stripeConfirmed" type="warning" :actions="[ { name: 'Setup Stripe account', href: '/account'  } ]">You must finish configuring your Stripe account before setting a price.</alert>
                </div>
              </div>
            </template>

            <div class="form-input series">
              <label class="form-label string" for="series">
                Series
              </label>
              <div v-if="series.length > 0" class="form-input-main">
                <select v-model="seriesId">
                  <option value=""></option>
                  <option v-for="s in series" :value="s.id">{{s.name}}</option>
                </select>
                <small class="form-note">
                  Select a Series to bundle this video with others. A Series of videos can be sold as one unit.
                </small>
              </div>
              <div v-else class="form-input-main">
                <em>
                  If you want to sell access to this video as part of a series, you'll need to
                  first <a href="/series/new">create a new series</a>
                </em>
              </div>
            </div>

            <div class="form-input categories">
              <label class="form-label string" for="categories">
                Categories
                <abbr title="required">*</abbr>

                <tooltip tooltip="Categories are like genres for your content. Each video must be in at least one category.<br>
                Examples: Full Productions, BTS, Orchestras, Ballets, Comedy, or Announcements" />
              </label>
              <div class="form-input-main">
                <vue-tags-input
                  v-model="tag"
                  :tags="tags"
                  placeholder="Add Category"
                  required
                  :autocomplete-items="autocompleteItems"
                  :add-only-from-autocomplete="true"
                  :autocomplete-min-length="0"
                  @tags-changed="newTags => tags = newTags" />
                  <small v-if="hasErrors('category_videos')" small class="form-error">{{errors.category_videos[0]}}</small>
              </div>
            </div>

            <div class="form-input string required image_file" v-bind:class="{ error: !imageValid || hasErrors('image_url') }">
              <label class="form-label string required">
                Thumbnail
                <abbr title="required">*</abbr>
                <tooltip tooltip="Design thumbnails at 1280×720 (16x9) so they stay sharp across all screen sizes. Use bold visuals, high contrast colors, and large, readable text that holds up even on mobile. Focus on one clear subject or moment, and avoid clutter. Save as JPG." />
              </label>
              <div class="form-input-main">
                <div v-if="imageUrl.length > 0" class="mb-3">
                  <img :src="imageUrl" class="rounded-md">
                </div>
                <dashboard-modal :uppy="imageUppy" :plugins="['ImageEditor']" :open="imageUploaderModalOpen" :props="dashboardModalOptions" />
                <btn :icon="false" variant="secondary" tag="button" accesskey="a" @click.native.prevent="openImageUploader">Choose Thumbnail Image</btn>
                <small v-if="hasErrors('image_url')" small class="form-error">{{errors.image_url[0]}}</small>
              </div>
            </div>

            <div class="form-input string required image_file" v-bind:class="{ error: !heroValid || hasErrors('hero_url') }">
              <label class="form-label string required">
                Background Image
                <tooltip tooltip="Design background images at a high 16x9 resolution. Do not use any text. Users will see background images at a larger size than thumbnails. Avoid clutter that will distract from the video’s description and details. Save as JPG." />
              </label>
              <div class="form-input-main">
                <div v-if="heroUrl.length > 0" class="mb-3">
                  <img :src="heroUrl" class="rounded-md">
                </div>
                <dashboard-modal :uppy="heroUppy" :plugins="['ImageEditor']" :open="heroUploaderModalOpen" :props="dashboardHeroModalOptions" />
                <btn :icon="false" variant="secondary" tag="button" accesskey="h" @click.native.prevent="openHeroUploader">Choose Background Image</btn>
                <small v-if="hasErrors('hero_url')" small class="form-error">{{errors.hero_url[0]}}</small>
              </div>
            </div>

            <div class="form-input shopify_custom_collection" v-if="false">
              <label class="form-label string">
                Shopify Collection
              </label>
              <div class="form-input-main">
                <div v-if="shopifyCollections.length > 0">
                  <select v-model="shopifyCollection">
                    <option value="0">No Collection</option>
                    <option v-for="c in shopifyCollections" :key="c.id" :value="c.id">
                      {{ c.title }}
                    </option>
                  </select>
                </div>
                <alert v-else>No collections set up yet.</alert>
              </div>
            </div>

            <div class="form-input required video_available" v-bind:class="{ error: hasErrors('date_available') }">
              <label class="form-label string required" for="video_available">
                Available At
                <abbr title="required">*</abbr>
                <small class="form-hint">Your timezone</small>
              </label>
              <div class="form-input-main">
                <datetime v-model="dateAvailable" type="datetime" :minute-step="15" input-class="w-sm" ></datetime>
                <small v-if="hasErrors('date_available')" small class="form-error">{{errors.date_available[0]}}</small>
              </div>
            </div>
            <div class="form-input required video_available" v-bind:class="{ error: hasErrors('date_end_available') }">
              <label class="form-label string required" for="video_available">
                Expiration
                <small class="form-hint">Leave blank for no expiry</small>
              </label>
              <div class="form-input-main flex">
                <datetime v-model="dateEndAvailable" type="datetime" :minute-step="15" input-class="md:w-sm" class="flex-1"></datetime>
                <small v-if="hasErrors('date_end_available')" small class="form-error">{{errors.date_end_available[0]}}</small>
                <button v-if="dateEndAvailable" class="ml-2 button-secondary inline-flex rounded-mg" @click="dateEndAvailable = ''">Clear</button>
              </div>
            </div>

            <div v-if="tenantLogoPresent" class="form-input boolean required video_published switch">
              <label class="form-label">Published</label>
              <div class="form-input-main">
                <div class="form-checkbox">
                  <div class="form-toggle">
                    <input v-model="published" id="switchPublished" type="checkbox" name="switchPublished">
                    <label for="switchPublished"/>
                  </div>
                </div>
                <small v-if="hasErrors('published')" small class="form-error">{{errors.published[0]}}</small>
              </div>
            </div>
            <div v-else class="form-input video_published">
              <label class="form-label">Published</label>
              <div class="form-input-main">
                <alert type="warning" :actions="[ { name: 'Upload channel logo', href: '/account'  } ]">Videos can't be published until a channel logo is uploaded.</alert>
              </div>
            </div>
          </form-inputs>

        </div>

      </div>

      <div v-if="errors && errors.delete" style="color: red">{{errors.delete}}</div>

      <form-actions>
        <btn v-if="purchases" variant="secondary" class="disabled:pointer-events-none" icon="circle-exclamation" tag="button" disabled="disabled">Video has views/purchases, cannot delete</btn>
        <btn v-if="featured" variant="secondary" class="disabled:pointer-events-none" icon="circle-exclamation" tag="button" disabled="disabled">Video featured on homepage, cannot delete</btn>
        <btn v-if="!purchases && !featured" icon="xmark" icon-placement="before" variant="secondary" tag="button" accesskey="d" @click.native.prevent="deleteVideo" :disabled="isSubmitting">Delete</btn>
        <btn type="submit" :icon="false" tag="button" accesskey="s" @click.native.prevent="saveVideo" class="disabled:bg-gray-300" :class="{'cursor-not-allowed': !formValid}" :disabled="!formValid || isSubmitting">Save Video</btn>
      </form-actions>

    </form>
  </div>
</template>

<script>
import { DashboardModal } from '@uppy/vue'
import VueTagsInput from '@johmun/vue-tags-input'
import { Datetime } from 'vue-datetime'

import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import '@uppy/image-editor/dist/style.css'
import 'vue-datetime/dist/vue-datetime.css'

import Uppy from '@uppy/core'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import ImageEditor from '@uppy/image-editor'

import videojs from 'video.js'

export default {
  props: {
    initCategories: Array,
    initCategoriesSelected: Array,
    initSlug: String,
    initName: String,
    initDescription: String,
    initIsSecure: Boolean,
    initImageUrl: String,
    initHeroUrl: String,
    initDateAvailable: String,
    initDateEndAvailable: String,
    initPublished: Boolean,
    initPricingModel: String,
    initVideoUrl: String,
    initPreviewVideoUrl: String,
    initPriceForm: String,
    initPriceBForm: String,
    initPriceCForm: String,
    initPriceMinForm: String,
    initPurchases: Boolean,
    initSeriesId: String,
    initShopifyCollection: Number,
    initShopifyCollections: Array,
    initStatus: String,
    tenantLogoPresent: Boolean,
    series: Array,
    stripeConfirmed: Boolean,
    featured: Boolean,
  },
  data() {
    return {
      slug: this.initSlug,
      name: this.initName || '',
      description: this.initDescription || '',
      isSecure: this.initIsSecure,
      imageUrl: this.initImageUrl || '',
      heroUrl: this.initHeroUrl || '',
      dateAvailable: this.initDateAvailable || '',
      dateEndAvailable: this.initDateEndAvailable || '',
      published: this.initPublished,
      videoUrl: this.initVideoUrl || '',
      videoStatus: this.initStatus || '',
      previewStart: '',
      previewEnd: '',
      previewProcessing: false,
      previewUrl: this.initPreviewVideoUrl || '',
      tag: '',
      tags: this.initCategoriesSelected,
      lastSavedTags: this.initCategoriesSelected,
      previousLastSavedTags: this.initCategoriesSelected,
      autocompleteItems: this.initCategories,
      errors: null,
      showFlash: false,
      isSubmitting: false,
      imageValid: true,
      heroValid: true,
      imageUploaderModalOpen: false,
      heroUploaderModalOpen: false,
      pricingModel: this.initPricingModel || 'free',
      price: this.initPriceForm || '',
      priceB: this.initPriceBForm || '',
      priceC: this.initPriceCForm || '',
      priceMin: this.initPriceMinForm || '',
      minimumPriceError: null,
      purchases: this.initPurchases || false,
      seriesId: this.initSeriesId || '',
      shopifyCollection: this.initShopifyCollection,
      shopifyCollections: this.initShopifyCollections,
      dashboardModalOptions: {
        onRequestCloseModal: this.closeUploader,
        closeAfterFinish: false,
        showLinkToFileUploadResult: false,
        autoOpenFileEditor: true, // This is 'autoOpen' in more recent versions
        note: "Image thumbnail should be 16:9 ratio. Click the pencil icon to crop if necessary.",
        locale: {
          strings: {
            dropPaste: 'Drop thumbnail file here, paste or %{browse}',
          }
        }
      },
      dashboardHeroModalOptions: {
        onRequestCloseModal: this.closeUploader,
        closeAfterFinish: false,
        showLinkToFileUploadResult: false,
        autoOpenFileEditor: true, // This is 'autoOpen' in more recent versions
        note: "Background image should be 16:9 ratio. Click the pencil icon to crop if necessary.",
        locale: {
          strings: {
            dropPaste: 'Drop background image file here, paste or %{browse}',
          }
        }
      },
    }
  },
  components: {
    DashboardModal,
    VueTagsInput,
    Datetime
  },
  computed: {
    preparingStatus: function() {
      return (this.videoStatus == 'preparing')
    },
    formValid: function() {
      if(this.pricingModel !== 'pay_what_you_can') {
        return true
      }
      if(! this.priceMin || ! this.price || ! this.priceB || ! this.priceC) {
        console.log('mising')
        return false
      }
      else if(
        + this.priceMin > +this.price ||
        + this.priceMin > +this.priceB ||
        + this.priceMin > +this.priceC
      ) {
        this.minimumPriceError = this.priceMin ? 'All suggested price options should be above the minimum price.' : null
        return false
      }
      else {
        this.minimumPriceError = null
        return true
      }
    }
  },
  beforeDestroy() {
    this.imageUppy.close()
    this.heroUppy.close()
  },
  beforeMount() {
    this.imageUppy = this.createUppy('image')
    this.heroUppy = this.createUppy('hero')
  },
  mounted() {

    if (!this.preparingStatus && this.videoUrl) {
      videojs(this.$refs.mainVideojs)
      if (this.previewUrl) {
        videojs(this.$refs.previewVideojs)
      }
    }

  },
  methods: {
    openImageUploader() {
      this.imageUploaderModalOpen = true
    },
    openHeroUploader() {
      this.heroUploaderModalOpen = true
    },
    closeUploader() {
      this.imageUploaderModalOpen = false
      this.heroUploaderModalOpen = false
    },
    createUppy(attr) {
      const uppyName = `${attr}Uppy`
      const attrName = `${attr}Url`
      return new Uppy({
          allowMultipleUploads: false,
          restrictions: {
            allowedFileTypes: ['image/*'],
            minNumberOfFiles: 1,
            maxNumberOfFiles: 1,
            maxTotalFileSize: 10485760
          }
        }).use(ImageEditor, {
          id: 'ImageEditor',
          quality: 0.9,
          actions: {
            revert: false,
            rotate: false,
            flip: false,
            zoomIn: true,
            zoomOut: true,
            cropSquare: false,
            cropWidescreen: false,
            cropWidescreenVertical: false
          },
          cropperOptions: {
            viewMode: 1,
            background: false,
            autoCropArea: 1,
            responsive: true,
            aspectRatio: 16/9,
          },
        }).use(AwsS3Multipart, {
          companionUrl: '/',
        }).on('file-editor:complete', (updatedFile) => {
          console.log("upload complete", uppyName, this)
          this[uppyName].upload()
        }).on('upload-success', (file, response) => {
          this[attrName] = response.uploadURL
          this[uppyName].cancelAll()
          this.closeUploader()
        })
    },
    // output: [
    //           { category_id: 1 },        // <-- added new
    //           { id: 6, _destroy: '1' },  // <-- delete
    //         ]
    getCatVideoAttrs() {
      // this.initCategoriesSelected is the array of exisiting categories
      //  [ {text: 'cat1', id: 3, category_id: 1}, {text: 'cat2', id: 4, category_id: 2} ]
      // this.initCategories is the array of all categories
      //  [ {text: 'cat1', category_id: 1}, {text: 'cat2', category_id: 2}, {text: 'cat3', category_id: 3} ]

      let arr = []
      const tagIds = this.tags.map(e => { return e.category_id })
      const oldIds = this.lastSavedTags.map(e => { return e.category_id })

      // deletions
      this.lastSavedTags.map(e => {
        if (tagIds.indexOf(e.category_id) == -1) {
          arr.push( { id: e.id, _destroy: '1'} )
        }
      })

      // additions
      this.tags.map(e => {
        if (oldIds.indexOf(e.category_id) == -1) {
          arr.push( { category_id: e.category_id } )
        }
      })

      // locally save current tag state for future edits
      this.previousLastSavedTags = this.lastSavedTags
      this.lastSavedTags = this.tags

      return arr
    },

    saveVideo() {
      this.isSubmitting = true

      let catVideoAttrs = this.getCatVideoAttrs()

      let params = {
        format: "json",
        video: {
          name: this.name,
          description: this.description,
          image_url: this.imageUrl,
          hero_url: this.heroUrl,
          published: this.published,
          date_available: this.dateAvailable,
          date_end_available: this.dateEndAvailable,
          preview_start: this.previewStart,
          preview_end: this.previewEnd,
          pricing_model: this.pricingModel,
          price_form: this.price,
          price_b_form: this.priceB,
          price_c_form: this.priceC,
          price_min_form: this.priceMin,
          series_id: this.seriesId,
          shopify_custom_collection_id: (this.shopifyCollection == 0) ? null : this.shopifyCollection
        }
      }

      if (catVideoAttrs.length > 0) {
        params['video']['category_videos_attributes'] = catVideoAttrs
      }

      let requestParams = {
        method: 'put',
        url: `/videos/${this.slug}`,
        data: params,
        headers: {
          'X-CSRF-Token': document.querySelectorAll(`meta[name="csrf-token"]`)[0].getAttribute('content')
        }
      }

      this.$http(requestParams)
        .then((response) => {
          if (response.data.success) {
            if (this.slug !== response.data.slug) {
              window.location = "/videos/" + response.data.slug
            }

            this.errors = null
            this.showFlash = true
            window.scrollTo(0,0)
            if (this.previewStart) {
              this.previewProcessing = true
            }
          } else {
            this.showFlash = false
            // Saving tags failed
            this.lastSavedTags = this.previousLastSavedTags
            this.errors = response.data.errors
            this.$nextTick(() =>  this.$refs.errorSummary.scrollIntoView())

          }
          this.isSubmitting = false
        }).catch((e) => {
          console.log(e)
        })
    },
    hasErrors(field) {
      return (this.errors && this.errors[field]);
    },
    deletePreview() {
      if (confirm("Are you sure?")) {
        this.isSubmitting = true

        let params = {
          format: 'json',
          video: {
            slug: this.slug,
          }
        }

        let requestParams = {
          method: 'delete',
          url: `/videos/${this.slug}/preview`,
          data: params,
          headers: {
            'X-CSRF-Token': document.querySelectorAll(`meta[name="csrf-token"]`)[0].getAttribute('content')
          }
        }

        this.$http(requestParams)
          .then((response) => {
            if (response.data.success) {
              // was running into some weird issues with the preview video player persisting after
              // vue removed its containing element. Refreshing the page fixes it.
              window.location.href = '/videos/' + this.slug
            } else {
              this.errors = response.data.errors
            }
            this.isSubmitting = false
        }).catch((e) => {
          console.log(e)
        })
      }
    },
    deleteVideo() {
      if (confirm("Are you sure?")) {

        this.isSubmitting = true

        let params = {
          format: 'json',
          video: {
            slug: this.slug,
          }
        }

        let requestParams = {
          method: 'delete',
          url: `/videos/${this.slug}`,
          data: params,
          headers: {
            'X-CSRF-Token': document.querySelectorAll(`meta[name="csrf-token"]`)[0].getAttribute('content')
          }
        }

        this.$http(requestParams)
          .then((response) => {
            if (response.data.success) {
              this.errors = null
              window.location.href = `/videos`
            } else {
              this.errors = response.data.errors
              this.isSubmitting = false
            }
        }).catch((e) => {
          console.log(e)
        })
      }

    },

  },
}
</script>
